<template>
  <main class="p-teamregistration">
    <section class="p-teamregistration__wrap">
      <h2 class="c-regist__title">{{ t('registerTeam.pageTitle') }}</h2>
      <RegisterFlow :steps="6" :nowStep="1" />
      <p class="c-regist__comment" v-html="t('registerTeam.message')"></p>
      <div class="p-teamregistration__contents">
        <div class="p-teamregistration__content">
          <p class="p-teamregistration__content-title">
            {{
              isMbpBuild
                ? t('registerTeam.teamNameInputLabelForMbp')
                : t('registerTeam.teamNameInputLabel')
            }}
          </p>
          <input-text
            ref="inFieldTeamName"
            v-model:input-value="registerTeamStore.teamName"
            name="teamName"
            class="p-teamregistration__content-text"
            :placeholder="
              isMbpBuild
                ? t('registerTeam.teamNameInputLabelForMbp')
                : t('registerTeam.teamNameInputLabel')
            "
            rules="required|teamNameFormat"
          />
        </div>
        <div class="p-teamregistration__content" v-if="!isMbpBuild">
          <p class="p-teamregistration__content-title">
            {{ t('registerTeam.sportTypeSelectLabel') }}
          </p>
          <select
            v-model="registerTeamStore.organizationType"
            class="p-teamregistration__content-option"
            name="organizationType"
          >
            <option class="p-teamregistration__content-option-item" disabled value="">
              {{ t('registerTeam.sportTypeSelect_noSelect') }}
            </option>
            <option
              v-for="organizationTypeMap in organizationTypeMaps"
              :key="organizationTypeMap.apiKey"
              :value="organizationTypeMap.apiKey"
              class="p-teamregistration__content-option-item"
              :disabled="organizationTypeMap.disabled"
            >
              {{ t(organizationTypeMap.displayCode) }}
            </option>
          </select>
        </div>
        <div class="p-teamregistration__content">
          <p class="p-teamregistration__content-title">
            {{ t('registerTeam.serverSelectLabel') }}
          </p>
          <select
            v-model="registerTeamStore.apiBaseUrl"
            class="p-teamregistration__content-option"
            name="apiBaseUrl"
          >
            <option class="p-teamregistration__content-option-item" disabled value="">
              {{ t('registerTeam.serverSelect_noSelect') }}
            </option>
            <option
              v-for="apiBaseUrlMap in apiBaseUrlMaps"
              :key="apiBaseUrlMap.key"
              :value="apiBaseUrlMap.value"
              class="p-teamregistration__content-option-item"
            >
              {{ t(apiBaseUrlMap.displayCode) }}
            </option>
          </select>
          <div
            v-if="maintenanceTitle && maintenanceMessage && !isServerAvailable"
            class="p-inputerror__error"
          >
            <div class="title">{{ maintenanceTitle }}</div>
            <div v-html="maintenanceMessage" class="message"></div>
          </div>
        </div>
        <div class="p-teamregistration__content" v-if="isMbpBuild">
          <p class="p-teamregistration__content-title">
            {{ t('registerTeam.courseSelectLabel') }}
          </p>
          <select
            v-model="registerTeamStore.courseId"
            class="p-teamregistration__content-option"
            name="course"
            :disabled="registerTeamStore.courseList.length === 0"
          >
            <option class="p-teamregistration__content-option-item" disabled value="">
              {{ t('registerTeam.courseSelect_noSelect') }}
            </option>
            <option
              v-for="course in registerTeamStore.courseList"
              :key="course.courseId"
              :value="course.courseId"
              class="p-teamregistration__content-option-item"
            >
              {{ course.name }}
            </option>
          </select>
        </div>
        <div class="p-teamregistration__link">
          <button
            class="c-button"
            v-on:click="onClickSubmit"
            :disabled="!isEnableButton"
            :class="{ isDisabled: !isEnableButton }"
          >
            <span class="c-button__text">{{ t('registerTeam.submitButton') }}</span>
          </button>
        </div>
      </div>
    </section>
    <!-- ↓ エラーモーダル / 「-opened」クラスの付け外しで表示非表示切り替え -->
    <section class="p-teamregistration__error jsc-modalerror__target">
      <div class="p-teamregistration__error-contents">
        <h2 class="p-teamregistration__error-title">
          {{ t('registerTeam.teamNameAlreadyRegisteredMessage') }}
        </h2>
        <dl class="p-teamregistration__error-info">
          <dt>{{ t('registerTeam.teamNameEntered') }}</dt>
          <dd>〇〇〇〇</dd>
        </dl>
        <p class="p-teamregistration__error-text">
          {{ t('registerTeam.confirmSendRequestToJoinTeamMessage') }}
        </p>
        <div class="p-teamregistration__error-link-wrap">
          <div class="p-teamregistration__error-link -fill">
            <a class="c-button"
              ><span class="c-button__text">{{
                t('registerTeam.confirmSendRequestToJoinTeam_Yes')
              }}</span></a
            >
          </div>
          <div class="p-teamregistration__error-link -stroke">
            <a class="p-teamregistration__error-link-item jsc-modalerror__trigger"
              ><span v-html="t('registerTeam.confirmSendRequestToJoinTeam_No')"></span
            ></a>
          </div>
        </div>
        <a class="p-teamregistration__error-close jsc-modalerror__trigger"></a>
      </div>
    </section>
    <!-- ↑ エラーモーダル / 「-opened」クラスの付け外しで表示非表示切り替え -->
  </main>
</template>

<script lang="ts">
import { defineComponent, computed, ref, watch, onMounted } from 'vue'
import { useI18n } from 'vue-i18n'
import { useRouter } from 'vue-router'
import RegisterFlow from '@/components/registerTeam/RegisterFlow.vue'
import StoreUtil from '@/stores/StoreUtil'
import InputText from '@/components/common/field/InputText.vue'
import useMaintenanceStatus from '@/Hooks/useMaintenanceStatus'

const organizationTypeMaps = [
  {
    displayCode: 'registerTeam.sportTypeSelect_soccer',
    apiKey: 'FOOTBALL',
    disabled: false,
  },
  {
    displayCode: 'registerTeam.sportTypeSelect_basketball',
    apiKey: 'BASKETBALL',
    disabled: false,
  },
  {
    displayCode: 'registerTeam.sportTypeSelect_rugby',
    apiKey: 'RUGBY',
    disabled: false,
  },
  {
    displayCode: 'registerTeam.sportTypeSelect_futsal',
    apiKey: 'FUTSAL',
    disabled: false,
  },
  {
    displayCode: 'registerTeam.sportTypeSelect_sports',
    apiKey: 'SPORTS',
    disabled: false,
  },
]

export default defineComponent({
  name: 'RegisterTeamPage',
  setup() {
    const inFieldTeamName = ref<InstanceType<typeof InputText>>()
    const router = useRouter()
    const { fetchMaintenanceStatus, title, message, availableStatus } = useMaintenanceStatus()
    const { organizationType } = router.currentRoute.value.query
    const { planType } = router.currentRoute.value.query
    const registerTeamStore = StoreUtil.useStore('RegisterTeamStore')
    // URL パラメーターに有効な組織種別があったらそれを初期設定する。
    if (organizationType) {
      const organizationTypes = organizationTypeMaps.map(typeMap => typeMap.apiKey)
      registerTeamStore.organizationType = organizationTypes.includes(
        (organizationType as string).toUpperCase(),
      )
        ? (organizationType as string).toUpperCase()
        : organizationTypes[0]
    }
    if (planType) registerTeamStore.planType = planType as string

    onMounted(() => {
      fetchMaintenanceStatus()
    })

    /**
     * MBP向けビルドかどうか
     */
    const isMbpBuild = computed(() => {
      return process.env.VUE_APP_BUILD_TARGET === 'mbp'
    })

    /**
     * 利用可能なサーバ一覧
     */
    const apiBaseUrlMaps = Object.entries(JSON.parse(process.env.VUE_APP_ALL_API_BASE_URL || {}))
      .filter(
        ([key]) => !((isMbpBuild.value || registerTeamStore.isOfflinePlan.value) && key === 'Asia'),
      )
      .map(([key, value]) => ({ key, value, displayCode: `registerTeam.serveSelect.${key}` }))

    /**
     * 選択したサーバが利用可能かどうか
     * メンテナンス中の場合 false を返す。
     */
    const isServerAvailable = computed(() => {
      const server = apiBaseUrlMaps.find(url => url.value === registerTeamStore.apiBaseUrl)?.key as
        | 'Asia'
        | 'Europe'
        | 'America'
        | undefined
      return server ? availableStatus.value[server] : true
    })

    /**
     * 登録ボタンの押下可否
     */
    const isEnableButton = computed(
      () =>
        inFieldTeamName.value?.meta.valid &&
        registerTeamStore.organizationType &&
        registerTeamStore.apiBaseUrl &&
        (!isMbpBuild.value || registerTeamStore.courseId) &&
        isServerAvailable.value,
    )

    watch(
      () => registerTeamStore.apiBaseUrl,
      async () => {
        if (isMbpBuild.value) {
          registerTeamStore.courseId = ''
          registerTeamStore.courseList = []
          await registerTeamStore.fetchCourseList()
        }
      },
    )

    return {
      isEnableButton,
      organizationTypeMaps,
      apiBaseUrlMaps,
      registerTeamStore,
      inFieldTeamName,
      isMbpBuild,
      ...useI18n(),
      onClickSubmit() {
        router.push({ name: 'AuthenticateUserPage' })
      },
      isServerAvailable,
      maintenanceTitle: title,
      maintenanceMessage: message,
    }
  },
  components: {
    RegisterFlow,
    'input-text': InputText,
  },
})
</script>

<style scoped lang="scss">
.isDisabled {
  opacity: 0.3;
  pointer-events: none;
}
</style>
